import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Label, Form, Input, Button } from 'reactstrap';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from "react-toastify";
import lmsLogo from '../assets/images/lmsLogo.png';
import { loginStudent } from '../helper/student_helper';
import { autoDetectDivision, clearDivisionCache } from '../helper/division_helper';
import Loader from '../component/Loader/Loader';
import Select from 'react-select';
import debounce from 'lodash/debounce';

const TataLogin = () => {
  const [loader, setLoader] = useState(false);
  const [detectingDivision, setDetectingDivision] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [divisionOptions, setDivisionOptions] = useState([]);
  const [showDivisionDropdown, setShowDivisionDropdown] = useState(false);
  const [detectedDivision, setDetectedDivision] = useState(null);
  const [detectionMessage, setDetectionMessage] = useState('');
  const [lastDetectedPhone, setLastDetectedPhone] = useState('');
  const [manualMode, setManualMode] = useState(false);
  const [inputType, setInputType] = useState('phone');
  const [showDivisionSelection, setShowDivisionSelection] = useState(false);
  const [pendingLogin, setPendingLogin] = useState(false);
  
  // State to handle username (phone or email), division selection, and password
  const [cred, setCred] = useState({ 
    username: "", 
    division: null,
    password: "" 
  });
  
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();
  
  // Refs
  const detectionAttempted = useRef(false);
  const loginAttempts = useRef(0);
  const usernameInputRef = useRef(null);

  // Function to validate email
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Function to validate phone
  const isValidPhone = (phone) => {
    return /^\d{10}$/.test(phone);
  };

  // Detect input type (phone or email)
  const detectInputType = (value) => {
    if (!value) return 'phone';
    
    // Check if it looks like an email (contains @ and .)
    if (value.includes('@') && value.includes('@')) {
      return 'email';
    }
    
    // Check if it's a valid phone (only digits, length 10)
    const digitsOnly = value.replace(/\D/g, '');
    if (digitsOnly.length === 10 && /^\d+$/.test(value)) {
      return 'phone';
    }
    
    // If it contains @ but not complete, still treat as email attempt
    if (value.includes('')) {
      return 'email';
    }
    
    return 'phone';
  };

  // Debounced function to detect division when phone number changes
  const detectDivisionDebounced = useCallback(
    debounce(async (username) => {
      // Only detect division for phone numbers (10 digits)
      if (username && username.length === 10 && /^\d+$/.test(username)) {
        // Don't detect again if we just detected for this phone
        if (username === lastDetectedPhone && detectionAttempted.current) {
          return;
        }
        
        setDetectingDivision(true);
        setDetectionMessage('Checking available divisions...');
        setManualMode(false);
        detectionAttempted.current = true;
        
        try {
          console.log('Checking divisions for phone:', username);
          
          // Auto attempt login to get division from error
          const result = await autoDetectDivision(username);
          
          setLastDetectedPhone(username);
          
          if (result.success && result.division) {
            // Division detected successfully
            const division = result.division;
            console.log('Division detected:', division);
            
            // Create option for the detected division
            const option = { value: division, label: division };
            
            // Auto-fill the division
            setCred(prev => ({ 
              ...prev, 
              division: option 
            }));
            
            setDetectedDivision(option);
            setShowDivisionDropdown(false);
            setShowDivisionSelection(false);
            setManualMode(false);
            setDetectionMessage(`Division detected: ${division}`);
            
            toast.success(`✓ Division detected: ${division}`, {
              position: "top-right",
              autoClose: 3000
            });
            
          } else if (result.allDivisions && result.allDivisions.length > 0) {
            // Multiple divisions found
            const options = result.allDivisions.map(div => ({
              value: div,
              label: div
            }));
            setDivisionOptions(options);
            setShowDivisionSelection(true);
            setDetectionMessage(`${result.allDivisions.length} divisions available`);
            
            console.log('Available divisions:', result.allDivisions);
            
          } else {
            // No division detected
            console.log('No divisions found for phone:', username);
            setDetectedDivision(null);
            setCred(prev => ({ ...prev, division: null }));
            setDetectionMessage('No divisions found');
            setManualMode(true);
          }
        } catch (error) {
          console.error('Error in division detection:', error);
          setDetectedDivision(null);
          setDetectionMessage('Error checking divisions');
          setManualMode(true);
        } finally {
          setDetectingDivision(false);
        }
      } else {
        // Reset division-related state for non-phone inputs
        setDivisionOptions([]);
        setShowDivisionDropdown(false);
        setShowDivisionSelection(false);
        setDetectedDivision(null);
        setCred(prev => ({ ...prev, division: null }));
        setDetectionMessage('');
        setDetectingDivision(false);
        setManualMode(false);
        detectionAttempted.current = false;
        loginAttempts.current = 0;
      }
    }, 1000),
    [lastDetectedPhone]
  );

  // Effect to detect input type and trigger division detection when username changes
  useEffect(() => {
    const type = detectInputType(cred.username);
    setInputType(type);
    
    // Only trigger division detection for phone numbers
    if (type === 'phone') {
      detectDivisionDebounced(cred.username);
    } else {
      // Cancel any pending detection for email
      detectDivisionDebounced.cancel();
      setDetectingDivision(false);
      setDetectionMessage('');
      setDetectedDivision(null);
      setShowDivisionSelection(false);
      setCred(prev => ({ ...prev, division: null }));
    }
    
    return () => {
      detectDivisionDebounced.cancel();
    };
  }, [cred.username, detectDivisionDebounced]);

  // Handle manual refresh of division detection
  const handleRefreshDetection = () => {
    if (inputType === 'phone' && cred.username && cred.username.length === 10) {
      clearDivisionCache(cred.username);
      setLastDetectedPhone('');
      detectionAttempted.current = false;
      loginAttempts.current = 0;
      setShowDivisionSelection(false);
      detectDivisionDebounced(cred.username);
      toast.info('Checking divisions...', { autoClose: 2000 });
    }
  };

  // Validate form based on input type
  const validate = () => {
    const e = {};
    
    // Username validation
    if (!cred.username?.trim()) {
      e.username = "Email or phone number is required";
    } else {
      const type = detectInputType(cred.username);
      
      if (type === 'phone') {
        // Phone validation
        const digitsOnly = cred.username.replace(/\D/g, '');
        if (!/^\d+$/.test(cred.username)) {
          e.username = "Phone number must contain only digits";
        } else if (digitsOnly.length !== 10) {
          e.username = "Phone number must be exactly 10 digits";
        }
      } else {
        // Email validation
        if (!isValidEmail(cred.username)) {
          e.username = "Please enter a valid email address";
        }
      }
    }
    
    // Password validation
    if (!cred.password) {
      e.password = "Password is required";
    } else if (cred.password.length < 4) {
      e.password = "Password must be at least 4 characters";
    }
    
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // Handle division selection and proceed with login
  const handleDivisionSelect = async (division) => {
    const option = { value: division, label: division };
    
    // Update state with selected division
    setCred(prev => ({ ...prev, division: option }));
    setDetectedDivision(option);
    setShowDivisionSelection(false);
    setManualMode(false);
    
    // Dismiss any existing toasts
    toast.dismiss();
    
    // Show selection confirmation
    toast.info(`Selected division: ${division}`, {
      position: "top-right",
      autoClose: 2000
    });
    
    // Proceed with login
    await performLogin(division);
  };

  // Function to perform login
  const performLogin = async (selectedDivision = null) => {
    try {
      setLoader(true);
      
      const division = selectedDivision || (cred.division ? cred.division.value : null);
      const phone = cred.username.trim();
      
      // Format username based on login type
      let usernameInput;
      if (division) {
        usernameInput = `${phone}-${division}`;
        console.log('Phone-Division login:', usernameInput);
      } else {
        usernameInput = phone;
        console.log('Phone only login:', usernameInput);
      }
      
      const loginData = {
        email: usernameInput,
        password: cred.password
      };
      
      console.log('Attempting login with:', usernameInput);
      
      const data = await loginStudent(loginData);
      console.log('Login response:', data);
      
      if (data?.success) {
        toast.success(data?.message || "Logged in successfully", {
          position: "top-right",
          autoClose: 3000
        });
        
        // Store token
        if (data?.token) {
          localStorage.setItem("token", data.token);
        }
        
        // Store student data
        const studentData = {
          ...(data?.studentDto || {}),
          loginType: division ? 'Phone-Division' : 'Phone',
          division: division || '',
          phone: phone,
          username: usernameInput,
          loginMethod: 'tata'
        };
        
        localStorage.setItem("student", JSON.stringify(studentData));
        
        if (division) {
          localStorage.setItem("userDivision", division);
        }
        localStorage.setItem("userPhone", phone);
        
        const authUser = {
          token: data.token,
          userType: 'Tata Student',
          userId: data?.studentDto?.id,
          division: division || '',
          phone: phone,
          studentData: studentData
        };
        localStorage.setItem("authUser", JSON.stringify(authUser));
        
        window.dispatchEvent(new Event('storage'));
        
        // Navigate to exams page
        setTimeout(() => {
          navigate("/myExams");
        }, 500);
        
      } else {
        const errorMsg = data?.message || "Login failed";
        console.log('Login failed:', errorMsg);
        
        // Check if error contains division information
        if (errorMsg.includes('Available divisions:')) {
          const match = errorMsg.match(/Available divisions?:?\s*(.*?)(?:\.|$)/);
          if (match && match[1]) {
            const divisions = match[1].split(',').map(d => d.trim());
            console.log('Divisions from error:', divisions);
            
            // Show division selection
            const options = divisions.map(div => ({
              value: div,
              label: div
            }));
            setDivisionOptions(options);
            setShowDivisionSelection(true);
            
            toast.info(
              <div>
                <p style={{marginBottom: '10px', fontWeight: 'bold'}}>Select your division:</p>
                <div style={{display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'center'}}>
                  {divisions.map((div, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleDivisionSelect(div)}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: '#0b3161',
                        color: 'white',
                        border: 'none',
                        borderRadius: '20px',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: '500'
                      }}
                    >
                      {div}
                    </button>
                  ))}
                </div>
              </div>,
              { autoClose: false }
            );
          } else {
            toast.error(errorMsg);
          }
        } else {
          toast.error(errorMsg);
        }
      }
    } catch (error) {
      console.error('Login error:', error);
      
      const errorMsg = error?.response?.data?.message || 
                      error?.message || 
                      "Something went wrong. Please try again.";
      
      // Check if error contains division information
      if (errorMsg.includes('Available divisions:')) {
        const match = errorMsg.match(/Available divisions?:?\s*(.*?)(?:\.|$)/);
        if (match && match[1]) {
          const divisions = match[1].split(',').map(d => d.trim());
          
          // Show division selection
          const options = divisions.map(div => ({
            value: div,
            label: div
          }));
          setDivisionOptions(options);
          setShowDivisionSelection(true);
          
          toast.info(
            <div>
              <p style={{marginBottom: '10px', fontWeight: 'bold'}}>Select your division:</p>
              <div style={{display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'center'}}>
                {divisions.map((div, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleDivisionSelect(div)}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#0b3161',
                      color: 'white',
                      border: 'none',
                      borderRadius: '20px',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: '500'
                    }}
                  >
                    {div}
                  </button>
                ))}
              </div>
            </div>,
            { autoClose: false }
          );
        } else {
          toast.error(errorMsg);
        }
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setLoader(false);
      setPendingLogin(false);
    }
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validate()) {
      return;
    }

    const type = detectInputType(cred.username);
    
    if (type === 'email') {
      // Handle email login
      try {
        setLoader(true);
        
        const loginData = {
          email: cred.username.trim().toLowerCase(),
          password: cred.password
        };
        
        const data = await loginStudent(loginData);
        
        if (data?.success) {
          toast.success(data?.message || "Logged in successfully");
          
          if (data?.token) localStorage.setItem("token", data.token);
          
          const studentData = {
            ...(data?.studentDto || {}),
            loginType: 'Email',
            email: cred.username,
            loginMethod: 'tata'
          };
          
          localStorage.setItem("student", JSON.stringify(studentData));
          localStorage.setItem("userEmail", cred.username);
          
          const authUser = {
            token: data.token,
            userType: 'Tata Student',
            userId: data?.studentDto?.id,
            email: cred.username,
            studentData: studentData
          };
          localStorage.setItem("authUser", JSON.stringify(authUser));
          
          window.dispatchEvent(new Event('storage'));
          setTimeout(() => navigate("/myExams"), 500);
        } else {
          toast.error(data?.message || "Login failed");
        }
      } catch (error) {
        toast.error(error?.message || "Login failed");
      } finally {
        setLoader(false);
      }
    } else {
      // For phone, check if division is already selected
      if (cred.division) {
        // Division already selected, proceed with login
        await performLogin();
      } else if (divisionOptions.length > 0) {
        // Multiple divisions available, show selection
        setShowDivisionSelection(true);
        toast.info(
          <div>
            <p style={{marginBottom: '10px', fontWeight: 'bold'}}>Please select your division:</p>
            <div style={{display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'center'}}>
              {divisionOptions.map((option, idx) => (
                <button
                  key={idx}
                  onClick={() => handleDivisionSelect(option.value)}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#0b3161',
                    color: 'white',
                    border: 'none',
                    borderRadius: '20px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: '500'
                  }}
                >
                  {option.value}
                </button>
              ))}
            </div>
          </div>,
          { autoClose: false }
        );
      } else {
        // No division info, attempt login to get divisions from error
        await performLogin();
      }
    }
  };

  const onChange = (key, value) => {
    setCred(prev => ({ ...prev, [key]: value }));
    setErrors(prev => ({ ...prev, [key]: undefined }));
  };

  const handleUsernameChange = (e) => {
    const value = e.target.value;
    
    // Detect input type
    const type = detectInputType(value);
    
    if (type === 'phone') {
      // For phone, only allow digits and limit to 10
      const digitsOnly = value.replace(/\D/g, '');
      if (digitsOnly.length <= 10) {
        onChange('username', digitsOnly);
      }
    } else {
      // For email, allow any characters
      onChange('username', value);
    }
  };

  const handleDivisionChange = (selectedOption) => {
    if (selectedOption) {
      handleDivisionSelect(selectedOption.value);
    } else {
      setCred(prev => ({ ...prev, division: null }));
      setDetectedDivision(null);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Get input placeholder
  const getUsernamePlaceholder = () => {
    return "Enter phone number or email address";
  };

  // Get input label
  const getUsernameLabel = () => {
    return "Phone or Email";
  };

  // Custom styles for react-select
  const selectStyles = {
    control: (provided, state) => ({
      ...provided,
      height: '44px',
      minHeight: '44px',
      borderRadius: '8px',
      borderColor: errors.division ? '#d9534f' : '#ddd',
      boxShadow: state.isFocused ? '0 0 0 2px rgba(11, 49, 97, 0.1)' : 'none',
      '&:hover': {
        borderColor: '#0b3161'
      },
      backgroundColor: detectedDivision ? '#f0fff0' : 'white'
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
      maxHeight: '300px'
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#0b3161' : state.isFocused ? '#e6f0ff' : 'white',
      color: state.isSelected ? 'white' : '#333',
      cursor: 'pointer',
      padding: '10px 12px',
      ':active': {
        backgroundColor: '#0b3161',
        color: 'white'
      }
    }),
    singleValue: (provided) => ({
      ...provided,
      color: detectedDivision ? '#2e7d32' : '#333',
      fontWeight: detectedDivision ? '500' : 'normal'
    })
  };

  return (
    <>
      {(loader || detectingDivision) && <Loader />}

      <style>{`
        .login-page {
          min-height: 100vh;
          width: 100%;
          display: flex;
          align-items: stretch;
          justify-content: center;
          background: #f6f9fc;
          overflow-x: hidden;
          font-family: Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial;
          margin: 0;
          padding: 0;
        }

        .login-wrap {
          width: 100%;
          margin: 0;
          border-radius: 0;
          overflow: hidden;
          display: flex;
          position: relative;
          background: transparent;
          min-height: 100vh;
        }

        .login-wrap .bg-decor {
          position: absolute;
          inset: 0;
          z-index: 0;
          background-size: cover;
          background-position: center center;
          opacity: 0.12;
          filter: blur(8px) saturate(0.9);
          transform: scale(1.02);
          pointer-events: none;
        }

        .hero-col {
          flex: 0 0 60%;
          min-width: 420px;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          padding: 48px;
          z-index: 2;
          overflow: hidden;
          background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%);
        }

        .hero-overlay {
          position: relative;
          z-index: 2;
          max-width: 720px;
          background: linear-gradient(90deg, rgba(0,0,0,0.46), rgba(0,0,0,0.18));
          border-radius: 12px;
          padding: 36px;
          color: #fff;
          box-shadow: 0 8px 30px rgba(2,6,23,0.18);
          transform: translateY(8px);
          animation: fadeSlideIn 700ms ease both;
        }

        .form-col {
          flex: 0 0 40%;
          min-width: 320px;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0;
          z-index: 2;
          background: transparent;
        }

        .form-card {
          width: 100%;
          height: 100%;
          display: block;
          max-width: none;
          border-radius: 0;
          background: #fff;
          padding: 40px;
          box-shadow: none;
          box-sizing: border-box;
        }

        .form-inner {
          width: 100%;
          max-width: 460px;
          margin: 0 auto;
        }

        .form-logo { 
          display:flex; 
          align-items:center; 
          gap:10px; 
          justify-content:center; 
          margin-bottom: 12px; 
        }

        .form-title { 
          text-align:center; 
          margin-bottom: 4px; 
          font-weight: 800;
          font-size: 1.8rem;
          color: #0b3161;
        }

        .form-sub { 
          text-align:center; 
          color: #6b7280; 
          margin-bottom: 24px; 
          font-size: 0.95rem; 
        }

        .form-row { 
          margin-bottom: 16px; 
          position: relative;
        }

        .split-row {
          display: flex;
          gap: 12px;
        }
        
        .split-row .form-row {
          flex: 1;
        }

        .input-field { 
          border-radius: 8px; 
          height: 44px; 
          padding: 8px 12px; 
          background-color: #fff !important;
          width: 100%;
          border: 1px solid #ddd;
          font-size: 14px;
          transition: all 0.3s ease;
        }

        .input-field:focus {
          border-color: #0b3161;
          box-shadow: 0 0 0 2px rgba(11, 49, 97, 0.1);
          outline: none;
        }

        .input-field:disabled {
          background-color: #f8f9fa;
          cursor: not-allowed;
        }

        .username-input-container {
          position: relative;
        }

        .input-type-badge {
          position: absolute;
          right: 10px;
          top: 12px;
          background: #e9ecef;
          padding: 2px 8px;
          border-radius: 12px;
          font-size: 11px;
          color: #495057;
          font-weight: 500;
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .phone-loader {
          position: absolute;
          right: 10px;
          top: 12px;
          color: #0b3161;
          font-size: 12px;
          background: white;
          padding: 0 5px;
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .phone-loader::after {
          content: '';
          width: 12px;
          height: 12px;
          border: 2px solid #0b3161;
          border-top-color: transparent;
          border-radius: 50%;
          animation: spinner 0.6s linear infinite;
        }

        @keyframes spinner {
          to { transform: rotate(360deg); }
        }

        .password-toggle-btn {
          position: absolute;
          right: 10px;
          top: 12px;
          background: transparent;
          border: none;
          color: #64748b;
          cursor: pointer;
          padding: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 3;
          font-size: 18px;
        }

        .password-toggle-btn:hover {
          color: #0b3161;
        }

        .forgot-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
          font-size: 14px;
        }

        .btn-primary { 
          background: linear-gradient(90deg, #0b3161, #1a4a82); 
          border: none; 
          color: #fff;
          font-weight: 600;
          padding: 12px 24px;
          border-radius: 8px;
          width: 100%;
          height: 44px;
          transition: all 0.3s ease;
          cursor: pointer;
          font-size: 14px;
        }

        .btn-primary:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(11, 49, 97, 0.3);
        }

        .btn-primary:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .form-foot { 
          text-align:center; 
          margin-top: 24px; 
          font-size: 14px; 
          color: #9aa4b2; 
        }

        .form-foot a {
          color: #0b3161;
          text-decoration: none;
          font-weight: 500;
        }

        .form-foot a:hover {
          text-decoration: underline;
        }

        .error-text {
          color: #d9534f;
          font-size: 13px;
          margin-top: 6px;
        }

        .checkbox-container {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .checkbox-container input[type="checkbox"] {
          width: 16px;
          height: 16px;
          border-radius: 4px;
          cursor: pointer;
        }

        .checkbox-container label {
          font-size: 14px;
          color: #64748b;
          cursor: pointer;
        }

        .detection-info {
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 11px;
          color: #6c757d;
          margin-top: 4px;
          min-height: 20px;
        }

        .refresh-btn {
          background: none;
          border: none;
          color: #0b3161;
          cursor: pointer;
          font-size: 11px;
          text-decoration: underline;
          padding: 0;
        }

        .refresh-btn:hover {
          color: #1a4a82;
        }

        .refresh-btn:disabled {
          color: #999;
          cursor: not-allowed;
          text-decoration: none;
        }

        .detected-badge {
          background-color: #e8f5e8;
          color: #2e7d32;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 11px;
          margin-top: 4px;
          text-align: center;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 4px;
        }

        .manual-mode-badge {
          background-color: #fff3cd;
          color: #856404;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 11px;
          margin-top: 4px;
          text-align: center;
          border: 1px solid #ffeeba;
        }

        .email-badge {
          background-color: #e3f2fd;
          color: #0d47a1;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 11px;
          margin-top: 4px;
          text-align: center;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 4px;
          border: 1px solid #bbdefb;
        }

        .division-selector {
          margin-top: 10px;
          padding: 15px;
          background-color: #f8f9fa;
          border-radius: 8px;
          border: 1px solid #dee2e6;
        }

        .division-buttons {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
          margin-top: 10px;
          justify-content: center;
        }

        .division-btn {
          padding: 10px 20px;
          background-color: #0b3161;
          color: white;
          border: none;
          border-radius: 25px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 600;
          transition: all 0.3s ease;
          min-width: 80px;
        }

        .division-btn:hover {
          background-color: #1a4a82;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(11, 49, 97, 0.3);
        }

        .react-select-container {
          width: 100%;
        }

        @keyframes fadeSlideIn { 
          from { opacity:0; transform: translateY(8px); } 
          to { opacity:1; transform: translateY(0); } 
        }

        @media (max-width: 991.98px) {
          .hero-col { display: none; }
          .form-col { flex: 1 1 100%; }
          .split-row { flex-direction: column; gap: 0; }
        }
      `}</style>

      <div className="login-page">
        <div className="login-wrap">
          <div className="bg-decor" />

          {/* Hero Section */}
          <div className="hero-col">
            <div className="hero-overlay">
              <div>
                <div style={{ fontSize: 34, fontWeight: 800, marginBottom: 8 }}>Tata Motors Login</div>
                <div style={{ marginBottom: 16, color: 'rgba(255,255,255,0.92)', fontSize: 16 }}>
                  Enter phone number or email to login
                </div>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 20 }}>
                  <span style={{ background: 'rgba(255,255,255,0.2)', padding: '6px 12px', borderRadius: 20, fontSize: 13 }}>📱 Phone + Division</span>
                  <span style={{ background: 'rgba(255,255,255,0.2)', padding: '6px 12px', borderRadius: 20, fontSize: 13 }}>✉️ Email Login</span>
                  <span style={{ background: 'rgba(255,255,255,0.2)', padding: '6px 12px', borderRadius: 20, fontSize: 13 }}>🔒 Secure Access</span>
                </div>
              </div>
            </div>
          </div>

          {/* Form Section */}
          <div className="form-col">
            <div className="form-card">
              <div className="form-inner">
                <div className="form-logo">
                  <Link to="/" aria-label="home">
                    {/* <img src={lmsLogo} alt="logo" height="44" /> */}
                  </Link>
                </div>

                <h3 className="form-title">Welcome Back</h3>
                <div className="form-sub">
                  {inputType === 'email' 
                    ? 'Login with your email address' 
                    : showDivisionSelection
                      ? 'Please select your division'
                      : detectedDivision
                        ? `Division: ${detectedDivision.label}`
                        : 'Enter phone number or email'}
                </div>

                <Form onSubmit={(e) => e.preventDefault()}>
                  {/* Username Input */}
                  <div className="form-row">
                    <Label htmlFor="username">
                      {getUsernameLabel()}
                      {inputType === 'phone' && cred.username && cred.username.length === 10 && !detectingDivision && detectedDivision && (
                        <span style={{ marginLeft: '8px', color: '#28a745', fontSize: '12px' }}>
                          ✓ Division ready
                        </span>
                      )}
                      {inputType === 'email' && cred.username && isValidEmail(cred.username) && (
                        <span style={{ marginLeft: '8px', color: '#28a745', fontSize: '12px' }}>
                          ✓ Valid email
                        </span>
                      )}
                    </Label>
                    <div className="username-input-container">
                      <Input 
                        id="username" 
                        name="username" 
                        value={cred.username} 
                        onChange={handleUsernameChange} 
                        className="input-field" 
                        placeholder={getUsernamePlaceholder()} 
                        type="text"
                        autoComplete="off"
                        ref={usernameInputRef}
                        disabled={showDivisionSelection}
                        style={{
                          paddingRight: inputType === 'email' || (inputType === 'phone' && cred.username?.length === 10) ? '80px' : '12px',
                          backgroundColor: showDivisionSelection ? '#f8f9fa' : '#fff'
                        }}
                      />
                      
                      {/* Show input type badge */}
                      {inputType === 'email' && cred.username && (
                        <span className="input-type-badge">
                          <span>✉️</span> Email
                        </span>
                      )}
                      
                      {inputType === 'phone' && cred.username && cred.username.length === 10 && !detectingDivision && !showDivisionSelection && (
                        <span className="input-type-badge">
                          <span>📱</span> Phone
                        </span>
                      )}
                      
                      {/* Show detection loader for phone */}
                      {inputType === 'phone' && detectingDivision && (
                        <span className="phone-loader">Checking</span>
                      )}
                    </div>
                    {errors.username && <div className="error-text">{errors.username}</div>}
                  </div>

                  {/* Division Selection Area */}
                  {inputType === 'phone' && showDivisionSelection && divisionOptions.length > 0 && (
                    <div className="division-selector">
                      <Label>Select your division/depot:</Label>
                      <div className="division-buttons">
                        {divisionOptions.map((option, index) => (
                          <button
                            key={index}
                            type="button"
                            className="division-btn"
                            onClick={() => handleDivisionSelect(option.value)}
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Division Display - when already selected */}
                  {inputType === 'phone' && !showDivisionSelection && detectedDivision && (
                    <div className="form-row">
                      <Label>Selected Division</Label>
                      <Input
                        value={detectedDivision.label}
                        className="input-field"
                        readOnly
                        style={{ 
                          backgroundColor: '#f0fff0', 
                          color: '#2e7d32', 
                          fontWeight: '500', 
                          borderColor: '#2e7d32'
                        }}
                      />
                    </div>
                  )}

                  {/* Detection Info */}
                  {inputType === 'phone' && cred.username?.length === 10 && !showDivisionSelection && !detectedDivision && (
                    <div className="detection-info">
                      <span>{detectionMessage || 'Click Login to check divisions'}</span>
                      {!detectingDivision && (
                        <button 
                          type="button"
                          onClick={handleRefreshDetection}
                          className="refresh-btn"
                        >
                          🔄 Refresh
                        </button>
                      )}
                    </div>
                  )}

                  {/* Email info badge */}
                  {inputType === 'email' && cred.username && (
                    <div className="email-badge">
                      <span>✉️</span> Email login - no division required
                    </div>
                  )}

                  {/* Password field */}
                  <div className="form-row">
                    <Label htmlFor="password">Password</Label>
                    <div style={{ position: 'relative' }}>
                      <Input 
                        id="password" 
                        name="password" 
                        value={cred.password} 
                        onChange={e => onChange('password', e.target.value)} 
                        className="input-field" 
                        type={showPassword ? 'text' : 'password'} 
                        placeholder="Enter your password"
                        autoComplete="new-password"
                        data-autocomplete="off"
                        disabled={showDivisionSelection}
                        style={{
                          backgroundColor: showDivisionSelection ? '#f8f9fa' : '#fff'
                        }}
                      />
                      <button 
                        type="button" 
                        className="password-toggle-btn" 
                        onClick={togglePasswordVisibility}
                        disabled={showDivisionSelection}
                      >
                        {showPassword ? '🙈' : '👁️'}
                      </button>
                    </div>
                    {errors.password && <div className="error-text">{errors.password}</div>}
                  </div>

                  {/* Remember me and forgot password */}
                  <div className="forgot-row">
                    <div className="checkbox-container">
                      <Input 
                        type="checkbox" 
                        id="remember" 
                        autoComplete="off"
                        name="remember"
                        disabled={showDivisionSelection}
                      />
                      <Label htmlFor="remember">Remember me</Label>
                    </div>
                    <div>
                      <Link to="/forgot-password" style={{ color: '#0b3161', fontWeight: 500, textDecoration: 'none' }}>
                        Forgot Password?
                      </Link>
                    </div>
                  </div>

                  {/* Submit button */}
                  <div className="form-row">
                    <Button 
                      color="primary" 
                      className="btn-primary" 
                      onClick={handleSubmit} 
                      disabled={
                        loader || 
                        detectingDivision || 
                        !cred.username || 
                        (inputType === 'phone' && cred.username.length !== 10) ||
                        showDivisionSelection
                      }
                      type="submit"
                    >
                      {loader ? 'Logging in...' : 
                       detectingDivision ? 'Checking...' : 
                       'Log In'}
                    </Button>
                  </div>

                  {/* Footer message */}
                  <div className="form-foot">
                    <small>
                      {showDivisionSelection 
                        ? 'Select your division to continue' 
                        : inputType === 'email' 
                          ? 'Login with email - no division needed'
                          : cred.username?.length === 10
                            ? detectedDivision 
                              ? 'Click Login to continue' 
                              : 'Click Login to check available divisions'
                            : 'Enter phone (10 digits) or email'}
                    </small>
                  </div>
                </Form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TataLogin;
